/**
 * Enhanced Color Swatch System - Dawn Theme Implementation
 * Uses Shopify's native swatch data (color.rgb and swatch.image)
 * Automatically displays exact hex colors from Shopify admin
 */

class SwatchEnhancement {
  constructor() {
    this.init();
  }

  init() {
    this.enhanceExistingSwatches();
    this.setupSwatchInteraction();
    this.observeSwatchChanges();
  }

  /**
   * Enhance existing swatches with Shopify's actual color/image data
   */
  enhanceExistingSwatches() {
    const swatches = document.querySelectorAll('.bls__product-color-swatches');
    
    swatches.forEach(swatch => {
      this.enhanceSwatch(swatch);
    });
  }

  /**
   * Enhance individual swatch with Shopify's swatch data
   */
  enhanceSwatch(swatch) {
    // Check if it's already enhanced
    if (swatch.dataset.enhanced === 'true') return;

    const colorValue = swatch.dataset.value || swatch.dataset.color;
    if (!colorValue) return;

    // Get Shopify swatch data from the product
    const swatchData = this.getShopifySwatchData(colorValue, swatch);
    
    if (swatchData) {
      this.applySwatchStyle(swatch, swatchData);
    }

    swatch.dataset.enhanced = 'true';
  }

  /**
   * Get Shopify's native swatch data from product variants
   */
  getShopifySwatchData(colorValue, swatchElement) {
    // Try to find the product data from the closest product context
    const productContainer = swatchElement.closest('[data-product-id]') || 
                           swatchElement.closest('.bls__product-item') ||
                           swatchElement.closest('.product-form') ||
                           document.querySelector('[data-product-id]');

    if (!productContainer) return null;

    // Look for product data in various places
    let productData = null;

    // Method 1: Check for product data in script tags
    const productScripts = document.querySelectorAll('script[type="application/json"]');
    for (const script of productScripts) {
      try {
        const data = JSON.parse(script.textContent);
        if (data.options_with_values || data.variants) {
          productData = data;
          break;
        }
      } catch (e) {
        // Continue to next script
      }
    }

    // Method 2: Check for global product data
    if (!productData && window.product) {
      productData = window.product;
    }

    // Method 3: Check for product data in meta tags or data attributes
    if (!productData) {
      const productMeta = document.querySelector('meta[name="product-data"]');
      if (productMeta) {
        try {
          productData = JSON.parse(productMeta.content);
        } catch (e) {
          // Continue
        }
      }
    }

    if (!productData) return null;

    // Find the color option and its values
    const colorOptions = productData.options_with_values || productData.options || [];
    
    for (const option of colorOptions) {
      // Check if this is a color option (case insensitive)
      const optionName = option.name.toLowerCase();
      if (optionName.includes('color') || optionName.includes('colour')) {
        
        // Find the matching value
        const values = option.values || [];
        const matchingValue = values.find(value => {
          const valueName = typeof value === 'string' ? value : value.value || value.name;
          return valueName && valueName.toLowerCase() === colorValue.toLowerCase();
        });

        if (matchingValue) {
          // Check if the value has swatch data
          const swatch = matchingValue.swatch || matchingValue;
          
          if (swatch) {
            // Check for color data
            if (swatch.color && swatch.color.rgb) {
              return {
                type: 'color',
                value: `rgb(${swatch.color.rgb})`
              };
            }
            
            // Check for image data
            if (swatch.image && swatch.image.src) {
              return {
                type: 'image',
                value: swatch.image.src,
                focalPoint: swatch.image.presentation ? swatch.image.presentation.focal_point : null
              };
            }
          }
        }
      }
    }

    // Alternative method: Check variants for swatch data
    if (productData.variants) {
      for (const variant of productData.variants) {
        // Check variant options
        for (let i = 1; i <= 3; i++) {
          const optionValue = variant[`option${i}`];
          if (optionValue && optionValue.toLowerCase() === colorValue.toLowerCase()) {
            // Check if this variant has swatch data
            if (variant.swatch) {
              if (variant.swatch.color && variant.swatch.color.rgb) {
                return {
                  type: 'color',
                  value: `rgb(${variant.swatch.color.rgb})`
                };
              }
              
              if (variant.swatch.image && variant.swatch.image.src) {
                return {
                  type: 'image',
                  value: variant.swatch.image.src,
                  focalPoint: variant.swatch.image.presentation ? variant.swatch.image.presentation.focal_point : null
                };
              }
            }
          }
        }
      }
    }

    return null;
  }

  /**
   * Apply swatch style based on Shopify data
   */
  applySwatchStyle(swatch, swatchData) {
    if (swatchData.type === 'color') {
      // Apply color background
      swatch.style.setProperty('--swatch-background', swatchData.value);
      swatch.style.background = swatchData.value;
      swatch.classList.add('swatch-enhanced');
      
      // Remove any existing background image
      swatch.style.backgroundImage = 'none';
      
    } else if (swatchData.type === 'image') {
      // Apply image background
      const imageUrl = swatchData.value.includes('?') ? 
        `${swatchData.value}&width=50` : 
        `${swatchData.value}?width=50`;
        
      swatch.style.setProperty('--swatch-background', `url(${imageUrl})`);
      swatch.style.backgroundImage = `url(${imageUrl})`;
      swatch.style.backgroundSize = 'cover';
      swatch.style.backgroundPosition = swatchData.focalPoint || 'center';
      swatch.style.backgroundOrigin = 'border-box';
      swatch.classList.add('swatch-enhanced', 'swatch-image');
      
      // Set focal point if available
      if (swatchData.focalPoint) {
        swatch.style.setProperty('--swatch-focal-point', swatchData.focalPoint);
      }
    }
  }

  /**
   * Setup swatch interaction handlers
   */
  setupSwatchInteraction() {
    document.addEventListener('click', (e) => {
      const swatch = e.target.closest('.bls__product-color-swatches');
      if (!swatch) return;

      const productTarget = swatch.closest('.bls__product-item');

      // Update active state
      const swatchGroup = swatch.closest('.bls__product-option');
      if (swatchGroup) {
        swatchGroup.querySelectorAll('.bls__product-color-swatches').forEach(s => {
          s.classList.remove('active');
        });
        swatch.classList.add('active');
      }

      // Trigger the main variant swap so button/label state updates immediately on click.
      // This is necessary because BlsColorSwatchesShopify uses mouseover + setTimeout(0),
      // and the click event fires before that timeout on fast clicks and touch devices,
      // leaving the swatch already marked active so the timeout skips swapProduct.
      if (productTarget && typeof BlsColorSwatchesShopify !== 'undefined' && BlsColorSwatchesShopify.swapProduct) {
        BlsColorSwatchesShopify.swapProduct(productTarget);
      }

      // Trigger variant change for other integrations (select elements, custom events)
      this.handleVariantChange(swatch);
    });
  }

  /**
   * Handle variant change
   */
  handleVariantChange(swatch) {
    const value = swatch.dataset.value;
    const position = swatch.dataset.position;
    
    if (value && position) {
      // Update any select elements
      const selects = document.querySelectorAll(`select[name*="option${position}"], select[name*="options"]`);
      selects.forEach(select => {
        const option = Array.from(select.options).find(opt => 
          opt.value === value || opt.textContent.trim() === value
        );
        if (option) {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      // Trigger custom variant change event
      const event = new CustomEvent('variant:change', {
        detail: { value, position },
        bubbles: true
      });
      swatch.dispatchEvent(event);
    }
  }

  /**
   * Observe for new swatches added dynamically
   */
  observeSwatchChanges() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const swatches = node.querySelectorAll ? 
              node.querySelectorAll('.bls__product-color-swatches') : [];
            
            swatches.forEach(swatch => {
              this.enhanceSwatch(swatch);
            });

            // Check if the node itself is a swatch
            if (node.classList && node.classList.contains('bls__product-color-swatches')) {
              this.enhanceSwatch(node);
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Refresh all swatches (useful for dynamic content)
   */
  refresh() {
    // Reset all enhanced swatches
    document.querySelectorAll('.bls__product-color-swatches[data-enhanced="true"]').forEach(swatch => {
      swatch.dataset.enhanced = 'false';
    });
    
    // Re-enhance all swatches
    this.enhanceExistingSwatches();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.swatchEnhancement = new SwatchEnhancement();
  });
} else {
  window.swatchEnhancement = new SwatchEnhancement();
}

// Export for manual initialization if needed
window.SwatchEnhancement = SwatchEnhancement;
